// Panel del Aliado v2 — mobile-first, PWA, bottom nav con 4 tabs.
// Diseño aprobado por Nico el 2026-09-16. Reemplaza al Dashboard viejo (tabular).
//
// Estructura:
// - Inicio: hero degradé con comisión del mes + share card protagonista + resumen.
// - Ventas: pedidos completos + historial comisiones.
// - Tienda: productos completos con botón "Compartir" (deep-link ?ref=SLUG&p=SKU).
// - Datos: perfil + acciones (instalar PWA, ver manual, cambiar pass, salir).
//
// El link al manual arma params dinámicos con los datos del aliado
// (?a=slug&n=NombreCorto&nf=NombreCompleto&e=email), evita el bug del
// manual que caía al default BIKO para cualquier otro aliado.

import { useEffect, useMemo, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/AuthContext'

// ═══════════════════════════════ Helpers ═════════════════════════════════

const NAVY = '#1A3461'
const TEAL = '#00A8B5'

const fmtUYU = (n) => `$${Math.round(Number(n || 0)).toLocaleString('es-UY')}`
const fmtUYUCorto = (n) => {
  const v = Number(n || 0)
  if (v >= 1000) return `$${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`
  return `$${Math.round(v)}`
}
const fmtUYUDec = (n) => `$${Number(n || 0).toLocaleString('es-UY', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
const fmtMes = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('es-UY', { month: 'long', year: 'numeric' }) : '—'

function haceCuanto(ts) {
  if (!ts) return '—'
  const d = new Date(ts)
  const hoy = new Date()
  const ayer = new Date(); ayer.setDate(ayer.getDate() - 1)
  const time = d.toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' })
  if (d.toDateString() === hoy.toDateString()) return `Hoy, ${time}`
  if (d.toDateString() === ayer.toDateString()) return `Ayer, ${time}`
  const diffDays = Math.floor((hoy - d) / 86400000)
  if (diffDays < 7) return `Hace ${diffDays} días`
  return d.toLocaleDateString('es-UY', { day: 'numeric', month: 'short' })
}

function saludo() {
  const h = new Date().getHours()
  if (h < 6) return 'Buenas noches,'
  if (h < 13) return 'Buenos días,'
  if (h < 20) return 'Buenas tardes,'
  return 'Buenas noches,'
}

function inicial(nombre) {
  return (nombre || '?').trim().charAt(0).toUpperCase()
}

function qrImgUrl(text, size = 220) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(text)}&color=1A3461&bgcolor=FFFFFF&margin=6`
}

const ESTADO_PILL = {
  pendiente_pago: { cls: 'va-pill-warn',    label: '⏱ Espera pago' },
  pagado:         { cls: 'va-pill-ok',      label: '✓ Pagado' },
  preparando:     { cls: 'va-pill-info',    label: '📦 Preparando' },
  en_armado:      { cls: 'va-pill-info',    label: '📦 En armado' },
  en_camino:      { cls: 'va-pill-info',    label: '🚚 En camino' },
  en_ruta:        { cls: 'va-pill-info',    label: '🚚 En ruta' },
  entregado:      { cls: 'va-pill-ok',      label: '✓ Entregado' },
  cancelado:      { cls: 'va-pill-err',     label: '✕ Cancelado' },
  rechazado:      { cls: 'va-pill-err',     label: '✕ Rechazado' },
  borrador:       { cls: 'va-pill-mute',    label: 'Borrador' },
}

const ESTADO_COMISION = {
  pendiente:         { cls: 'va-pill-warn', label: 'PENDIENTE' },
  hold_verificacion: { cls: 'va-pill-info', label: 'HOLD' },
  pagada:            { cls: 'va-pill-ok',   label: 'PAGADO ✓' },
  cancelada:         { cls: 'va-pill-err',  label: 'CANCELADO' },
}

// ═══════════════════════════════ PWA install hook ═════════════════════════

function usePWAInstall() {
  const [deferred, setDeferred] = useState(null)
  const [installed, setInstalled] = useState(false)

  useEffect(() => {
    // Ya corre standalone (instalada)
    if (typeof window === 'undefined') return
    const isStandalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    if (isStandalone) { setInstalled(true); return }

    const onBefore = (e) => { e.preventDefault(); setDeferred(e) }
    const onInstalled = () => { setInstalled(true); setDeferred(null) }
    window.addEventListener('beforeinstallprompt', onBefore)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBefore)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  const isIOS = typeof navigator !== 'undefined' &&
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream

  const install = async () => {
    if (!deferred) return null
    deferred.prompt()
    const { outcome } = await deferred.userChoice
    setDeferred(null)
    return outcome  // 'accepted' | 'dismissed'
  }

  return { canInstall: !!deferred, installed, install, isIOS }
}

// ═══════════════════════════════ Main ═════════════════════════════════════

export default function VendedorDashboard() {
  const { user, profile, role, signOut } = useAuth()
  const [searchParams] = useSearchParams()
  const vidParam = searchParams.get('vid')
  const esAdmin = role === 'admin' || role === 'superadmin'
  const impersonando = !!vidParam && vidParam !== user?.id && esAdmin
  const vidEfectivo = impersonando ? vidParam : user?.id

  const [tab, setTab] = useState(() => {
    try { return sessionStorage.getItem('va_tab') || 'inicio' } catch { return 'inicio' }
  })
  useEffect(() => { try { sessionStorage.setItem('va_tab', tab) } catch { /* */ } }, [tab])
  useEffect(() => { window.scrollTo(0, 0) }, [tab])

  const [stats, setStats] = useState(null)
  const [vendedor, setVendedor] = useState(null)
  const [pedidos, setPedidos] = useState([])
  const [clientes, setClientes] = useState([])
  const [comisiones, setComisiones] = useState([])
  const [productos, setProductos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!user?.id) return
    cargar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, vidEfectivo])

  async function cargar() {
    setLoading(true); setError(null)
    try {
      const rpcParam = impersonando ? { vendedor_id_param: vidEfectivo } : undefined
      const [
        { data: dash, error: e1 },
        { data: v, error: e2 },
        { data: ped, error: e3 },
        { data: cli, error: e4 },
        { data: com, error: e5 },
        { data: prod, error: e6 },
      ] = await Promise.all([
        supabase.rpc('dashboard_vendedor', rpcParam),
        supabase.from('porteros').select('slug, nombre_completo, email, comision_default_pct, activo, created_at').eq('id', vidEfectivo).maybeSingle(),
        supabase.rpc('vendedor_get_mis_pedidos', rpcParam),
        supabase.rpc('vendedor_get_mis_clientes', rpcParam),
        supabase.from('comisiones')
          .select('id, mes, monto_total, cantidad_clientes, estado, fecha_pago')
          .eq('portero_id', vidEfectivo)
          .order('mes', { ascending: false }),
        supabase.rpc('vendedor_get_mis_productos', rpcParam),
      ])
      const firstErr = e1 || e2 || e3 || e4 || e5 || e6
      if (firstErr) throw firstErr
      setStats(dash?.[0] ?? null)
      setVendedor(v ?? null)
      setPedidos(ped || [])
      setClientes(cli || [])
      setComisiones(com || [])
      setProductos(prod || [])
    } catch (e) {
      console.error('cargar vendedor:', e)
      setError(e.message || 'Error cargando datos')
    } finally {
      setLoading(false)
    }
  }

  // Delta comisión vs mes anterior (calculado client-side desde comisiones histórico)
  const deltaComision = useMemo(() => {
    if (!stats || !comisiones?.length) return null
    const anterior = comisiones[1]?.monto_total || comisiones[0]?.monto_total
    const actual = stats.comision_mes_actual
    if (!anterior || anterior <= 0) return null
    const pct = Math.round(((actual - anterior) / anterior) * 100)
    return { pct, positivo: pct >= 0 }
  }, [stats, comisiones])

  const nombreCorto = (impersonando ? vendedor?.nombre_completo : profile?.nombre || vendedor?.nombre_completo)?.split(' ')?.[0] || 'Aliado'
  const nombreCompleto = vendedor?.nombre_completo || profile?.nombre || 'Aliado'
  const emailAliado = vendedor?.email || profile?.email || ''
  const codigo = vendedor?.slug?.toUpperCase() || ''
  const refUrl = vendedor?.slug ? `https://limpack.uy/?ref=${codigo}` : null
  const manualUrl = vendedor?.slug
    ? `/manual-aliado-muxf2qov1y.html?${new URLSearchParams({
        a: vendedor.slug, n: nombreCorto, nf: nombreCompleto, e: emailAliado
      }).toString()}`
    : '/manual-aliado-muxf2qov1y.html'

  if (loading) {
    return (
      <div className="va-loading">
        <style>{css}</style>
        <div className="va-spinner" />
        <div>Cargando tu panel…</div>
      </div>
    )
  }
  if (error) {
    return (
      <div className="va-error">
        <style>{css}</style>
        <div style={{ fontSize: 40, marginBottom: 8 }}>⚠️</div>
        <div>Error cargando datos</div>
        <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 8 }}>{error}</div>
      </div>
    )
  }

  const ctx = {
    stats, vendedor, pedidos, clientes, comisiones, productos,
    profile, nombreCorto, nombreCompleto, emailAliado, codigo, refUrl,
    manualUrl, deltaComision, signOut, impersonando,
  }

  return (
    <>
      <style>{css}</style>
      <div className="va-app">
        {impersonando && (
          <div className="va-impersonar">
            Viendo panel de <strong>{vendedor?.nombre_completo}</strong>
            <Link to="/admin/vendedores">← Admin</Link>
          </div>
        )}

        <div className="va-scroll">
          {tab === 'inicio'  && <TabInicio ctx={ctx} onTab={setTab} />}
          {tab === 'ventas'  && <TabVentas ctx={ctx} />}
          {tab === 'tienda'  && <TabTienda ctx={ctx} />}
          {tab === 'datos'   && <TabDatos  ctx={ctx} />}
        </div>

        <BottomNav tab={tab} onTab={setTab} />
      </div>
    </>
  )
}

// ═══════════════════════════════ TAB Inicio ═══════════════════════════════

function TabInicio({ ctx, onTab }) {
  const { stats, nombreCorto, codigo, refUrl, productos, pedidos, deltaComision } = ctx
  const [copiado, setCopiado] = useState(false)
  const [qrGrande, setQrGrande] = useState(false)

  const copiar = (t) => {
    navigator.clipboard?.writeText(t)
    setCopiado(true); setTimeout(() => setCopiado(false), 2000)
  }
  const compartirWhatsApp = () => {
    const msg = [
      `¡Hola! Soy ${nombreCorto}, aliado de *Limpack* — productos de limpieza a domicilio en Montevideo y Canelones.`,
      '',
      `Tienen *LimpackFlex*: si comprás antes de las 16 h, te llega hoy.`,
      '',
      `Con mi código *${codigo}* tenés 10% off a partir de $1.500 en compras, y además se habilita el pago por transferencia directa (así no cargás la tarjeta).`,
      '',
      refUrl,
    ].join('\n')
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank')
  }

  const top4 = productos.slice(0, 4)
  const last3 = pedidos.slice(0, 3)

  return (
    <>
      {/* ─── Hero ─── */}
      <div className="va-hero">
        <div className="va-hero-top">
          <div className="va-brand">Lim<em>pack</em></div>
          <div className="va-avatar">{inicial(nombreCorto)}</div>
        </div>
        <p className="va-saludo">{saludo()}</p>
        <h1 className="va-nombre">{nombreCorto} 👋</h1>

        <div className="va-metric-label">Ganaste este mes</div>
        <div className="va-metric-big">{fmtUYU(stats?.comision_mes_actual)}</div>
        {deltaComision && (
          <div className="va-metric-delta">
            <span>{deltaComision.positivo ? '↑' : '↓'} {Math.abs(deltaComision.pct)}%</span>
            <span style={{ opacity: 0.75 }}>vs. mes anterior</span>
          </div>
        )}

        <div className="va-mini-kpis">
          <div className="va-mini-kpi">
            <div className="va-mini-val">{stats?.clientes_activos ?? 0}</div>
            <div className="va-mini-lbl">Clientes activos</div>
          </div>
          <div className="va-mini-kpi">
            <div className="va-mini-val">{stats?.pedidos_mes_actual ?? 0}</div>
            <div className="va-mini-lbl">Pedidos mes</div>
          </div>
          <div className="va-mini-kpi">
            <div className="va-mini-val">{fmtUYUCorto(stats?.total_pagado_historico)}</div>
            <div className="va-mini-lbl">Total cobrado</div>
          </div>
        </div>
      </div>

      {/* ─── Share card ─── */}
      <div className="va-share">
        <div className="va-share-title">🔗 Compartí tu link</div>
        <div className="va-share-row">
          <button
            className="va-qr va-qr-btn"
            onClick={() => setQrGrande(true)}
            title="Tocá para ampliar"
            aria-label="Ampliar QR"
          >
            {refUrl && <img src={qrImgUrl(refUrl, 220)} alt={`QR ${codigo}`} loading="lazy" />}
            <span className="va-qr-zoom">🔍</span>
          </button>
          <div className="va-share-info">
            <div className="va-share-codigo">{codigo}</div>
            <div className="va-share-link">limpack.uy/?ref={codigo}</div>
          </div>
        </div>
        <div className="va-share-actions">
          <button className="va-btn va-btn-copy" onClick={() => copiar(refUrl)}>
            {copiado ? '✓ Copiado' : '📋 Copiar link'}
          </button>
          <button className="va-btn va-btn-wa" onClick={compartirWhatsApp}>💬 WhatsApp</button>
        </div>
      </div>

      {/* ─── Últimos pedidos ─── */}
      <div className="va-section">
        <div className="va-section-title">
          <span>Últimos pedidos</span>
          {pedidos.length > 0 && <a onClick={() => onTab('ventas')}>Ver todos →</a>}
        </div>
        {last3.length === 0 ? (
          <EmptyPedidos />
        ) : (
          last3.map(p => <PedidoCard key={p.id} p={p} />)
        )}
      </div>

      {/* ─── Productos destacados ─── */}
      <div className="va-section">
        <div className="va-section-title">
          <span>Productos para compartir</span>
          {productos.length > 4 && <a onClick={() => onTab('tienda')}>Ver todos →</a>}
        </div>
        {top4.length === 0 ? (
          <div className="va-empty">Sin productos.</div>
        ) : (
          <div className="va-prod-grid">
            {top4.map(p => <ProductoCard key={p.id} p={p} slug={ctx.vendedor?.slug} />)}
          </div>
        )}
      </div>

      {/* ─── QR ampliado (overlay fullscreen sin data privada) ─── */}
      {qrGrande && refUrl && (
        <QRFullscreen
          refUrl={refUrl}
          codigo={codigo}
          nombreCorto={nombreCorto}
          onClose={() => setQrGrande(false)}
        />
      )}
    </>
  )
}

// Overlay del QR ampliado. Diseñado para pasar el celu a alguien o compartir
// como imagen. NO muestra ningún dato privado del aliado (nada de ventas,
// comisiones, clientes) — solo QR + código + link.
function QRFullscreen({ refUrl, codigo, nombreCorto, onClose }) {
  const [descargando, setDescargando] = useState(false)
  const [compartiendo, setCompartiendo] = useState(false)
  const [copiado, setCopiado] = useState(false)

  async function fetchQrBlob() {
    // QR 1024px para calidad al imprimir/redes
    const url = qrImgUrl(refUrl, 1024)
    const res = await fetch(url)
    if (!res.ok) throw new Error('No se pudo descargar el QR')
    return await res.blob()
  }

  async function descargar() {
    setDescargando(true)
    try {
      const blob = await fetchQrBlob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `limpack-${codigo.toLowerCase()}.png`
      document.body.appendChild(a); a.click(); a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (e) {
      // Fallback: abre en nueva pestaña para tap-and-hold
      window.open(qrImgUrl(refUrl, 1024), '_blank')
    } finally {
      setDescargando(false)
    }
  }

  async function compartir() {
    setCompartiendo(true)
    try {
      // Preferimos compartir como archivo (aparece opción "Enviar por WhatsApp
      // como imagen"). Requiere navigator.canShare con files (Chrome, Safari 15+).
      const blob = await fetchQrBlob()
      const file = new File([blob], `limpack-${codigo.toLowerCase()}.png`, { type: 'image/png' })
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `Código ${codigo} · Limpack`,
          text: `Escaneá este QR para comprar en Limpack con mi código ${codigo}.`,
        })
      } else if (navigator.share) {
        await navigator.share({
          title: `Código ${codigo} · Limpack`,
          text: `Escaneá para comprar en Limpack con mi código ${codigo}: ${refUrl}`,
          url: refUrl,
        })
      } else {
        // Sin Web Share → copiamos el link como fallback
        navigator.clipboard?.writeText(refUrl)
        setCopiado(true); setTimeout(() => setCopiado(false), 2000)
      }
    } catch (e) {
      // Cancelado por el usuario o error → silencio
    } finally {
      setCompartiendo(false)
    }
  }

  return (
    <div className="va-qr-back" onClick={onClose}>
      <div className="va-qr-modal" onClick={e => e.stopPropagation()}>
        <button className="va-qr-close" onClick={onClose} aria-label="Cerrar">✕</button>
        <div className="va-qr-modal-brand">Lim<em>pack</em></div>
        <div className="va-qr-modal-sub">Aliado · {nombreCorto}</div>
        <div className="va-qr-xl-wrap">
          <img className="va-qr-xl" src={qrImgUrl(refUrl, 600)} alt={`QR ${codigo}`} />
        </div>
        <div className="va-qr-modal-codigo">{codigo}</div>
        <div className="va-qr-modal-link">limpack.uy/?ref={codigo}</div>

        <div className="va-qr-actions">
          <button className="va-btn va-btn-primary-navy" onClick={compartir} disabled={compartiendo}>
            {copiado ? '✓ Link copiado' : compartiendo ? 'Abriendo…' : '📱 Compartir imagen'}
          </button>
          <button className="va-btn va-btn-copy" onClick={descargar} disabled={descargando}>
            {descargando ? 'Bajando…' : '📥 Descargar QR'}
          </button>
        </div>

        <div className="va-qr-modal-hint">
          Podés pasar el celu para que escaneen, o compartir la imagen del QR
          por WhatsApp / Instagram.
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════ TAB Ventas ═══════════════════════════════

function TabVentas({ ctx }) {
  const { pedidos, stats, comisiones } = ctx
  const [filtro, setFiltro] = useState('todos')

  const filtrado = useMemo(() => {
    if (filtro === 'pagados')    return pedidos.filter(p => p.estado === 'pagado' || p.estado === 'entregado')
    if (filtro === 'camino')     return pedidos.filter(p => ['preparando','en_armado','en_camino','en_ruta'].includes(p.estado))
    if (filtro === 'pendientes') return pedidos.filter(p => p.estado === 'pendiente_pago')
    return pedidos
  }, [pedidos, filtro])

  return (
    <>
      <div className="va-subheader">
        <h2>Mis ventas</h2>
        <p>
          {stats?.pedidos_mes_actual ?? 0} pedidos este mes · {fmtUYU(stats?.ventas_mes_actual)} en ventas · {fmtUYU(stats?.comision_mes_actual)} tu comisión
        </p>
      </div>

      <div className="va-chips">
        <button className={`va-chip ${filtro === 'todos' ? 'is-active' : ''}`} onClick={() => setFiltro('todos')}>Todos ({pedidos.length})</button>
        <button className={`va-chip ${filtro === 'pagados' ? 'is-active' : ''}`} onClick={() => setFiltro('pagados')}>Pagados</button>
        <button className={`va-chip ${filtro === 'camino' ? 'is-active' : ''}`} onClick={() => setFiltro('camino')}>En camino</button>
        <button className={`va-chip ${filtro === 'pendientes' ? 'is-active' : ''}`} onClick={() => setFiltro('pendientes')}>Pendientes</button>
      </div>

      <div className="va-section" style={{ paddingTop: 0 }}>
        {filtrado.length === 0 ? (
          <EmptyPedidos />
        ) : (
          filtrado.map(p => <PedidoCard key={p.id} p={p} />)
        )}
      </div>

      {comisiones.length > 0 && (
        <>
          <div className="va-section-title" style={{ padding: '0 20px' }}>Historial de comisiones</div>
          <div className="va-section">
            {comisiones.map(c => {
              const cfg = ESTADO_COMISION[c.estado] || { cls: 'va-pill-mute', label: c.estado }
              return (
                <div key={c.id} className="va-hist-item">
                  <div>
                    <div className="va-hist-mes">{fmtMes(c.mes)}</div>
                    <div className="va-hist-sub">{c.cantidad_clientes} pedidos</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="va-hist-monto">{fmtUYU(c.monto_total)}</div>
                    <div className={`va-hist-estado ${cfg.cls}`}>{cfg.label}</div>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
    </>
  )
}

// ═══════════════════════════════ TAB Tienda ═══════════════════════════════

function TabTienda({ ctx }) {
  const { productos, vendedor } = ctx
  const [q, setQ] = useState('')
  const [cat, setCat] = useState('todas')

  const categorias = useMemo(() => {
    const map = new Map()
    productos.forEach(p => map.set(p.categoria || 'otros', (map.get(p.categoria || 'otros') || 0) + 1))
    return [...map.entries()].sort((a, b) => b[1] - a[1])
  }, [productos])

  const filtrados = useMemo(() => {
    let arr = productos
    if (cat !== 'todas') arr = arr.filter(p => (p.categoria || 'otros') === cat)
    if (q.trim()) {
      const s = q.toLowerCase()
      arr = arr.filter(p => (p.nombre || '').toLowerCase().includes(s) || (p.sku || '').toLowerCase().includes(s))
    }
    return arr
  }, [productos, cat, q])

  return (
    <>
      <div className="va-subheader">
        <h2>Productos</h2>
        <p>Tocá "Compartir" para copiar el link de la ficha con tu código</p>
      </div>

      <div style={{ padding: '12px 16px 8px' }}>
        <input
          type="search"
          placeholder="🔍 Buscar producto…"
          value={q}
          onChange={e => setQ(e.target.value)}
          className="va-search"
        />
      </div>

      <div className="va-chips" style={{ paddingTop: 0 }}>
        <button className={`va-chip ${cat === 'todas' ? 'is-active' : ''}`} onClick={() => setCat('todas')}>Todas ({productos.length})</button>
        {categorias.map(([c, n]) => (
          <button key={c} className={`va-chip ${cat === c ? 'is-active' : ''}`} onClick={() => setCat(c)}>{c} ({n})</button>
        ))}
      </div>

      <div className="va-section" style={{ paddingTop: 0 }}>
        {filtrados.length === 0 ? (
          <div className="va-empty">Sin productos que coincidan.</div>
        ) : (
          <div className="va-prod-grid">
            {filtrados.map(p => <ProductoCard key={p.id} p={p} slug={vendedor?.slug} />)}
          </div>
        )}
      </div>
    </>
  )
}

// ═══════════════════════════════ TAB Datos ═══════════════════════════════

function TabDatos({ ctx }) {
  const { vendedor, nombreCompleto, emailAliado, codigo, manualUrl, stats, signOut } = ctx
  const pwa = usePWAInstall()

  const [instrIOSVisible, setInstrIOSVisible] = useState(false)

  const handleInstall = async () => {
    if (pwa.canInstall) {
      await pwa.install()
    } else if (pwa.isIOS) {
      setInstrIOSVisible(true)
    } else {
      alert('Para instalarla, abrí el panel en Chrome o Safari desde tu celular.')
    }
  }

  const created = vendedor?.created_at
    ? new Date(vendedor.created_at).toLocaleDateString('es-UY', { month: 'short', year: 'numeric' })
    : '—'

  return (
    <>
      <div className="va-subheader">
        <h2>Mi cuenta</h2>
        <p>Datos de contacto y configuración</p>
      </div>

      <div className="va-datos-card">
        <div className="va-datos-header">
          <div className="va-datos-avatar">{inicial(nombreCompleto)}</div>
          <div>
            <div className="va-datos-nombre">{nombreCompleto}</div>
            <div className="va-datos-sub">Aliado desde {created}</div>
          </div>
        </div>
        <div className="va-datos-row"><span className="k">Email</span><span className="v">{emailAliado || '—'}</span></div>
        <div className="va-datos-row"><span className="k">Código</span><span className="v" style={{ color: TEAL }}>{codigo || '—'}</span></div>
        <div className="va-datos-row"><span className="k">Comisión default</span><span className="v">{vendedor?.comision_default_pct ?? '—'}%</span></div>
        <div className="va-datos-row"><span className="k">Total histórico</span><span className="v">{fmtUYU(stats?.total_pagado_historico)}</span></div>
        <div className="va-datos-row"><span className="k">Ganancias este mes</span><span className="v" style={{ color: TEAL }}>{fmtUYU(stats?.comision_mes_actual)}</span></div>
      </div>

      <div className="va-datos-titulo-acciones">Acciones</div>

      {!pwa.installed && (
        <button className="va-datos-action va-action-install" onClick={handleInstall}>
          📲 Instalar como app en tu celu
        </button>
      )}

      <a className="va-datos-action va-action-primary" href={manualUrl} target="_blank" rel="noreferrer">
        📖 Ver mi manual del aliado
      </a>

      <a className="va-datos-action va-action-secondary" href="/reset-password">
        🔑 Cambiar contraseña
      </a>

      <button className="va-datos-action va-action-danger" onClick={signOut}>
        Salir
      </button>

      <div className="va-foot">Limpack — Panel del Aliado v2.0</div>

      {/* iOS install instructions modal */}
      {instrIOSVisible && (
        <div className="va-modal-back" onClick={() => setInstrIOSVisible(false)}>
          <div className="va-modal" onClick={e => e.stopPropagation()}>
            <div className="va-modal-title">📲 Instalar en iPhone</div>
            <ol style={{ paddingLeft: 20, margin: '12px 0 16px', fontSize: 13, color: '#334155', lineHeight: 1.6 }}>
              <li>Tocá el botón <strong>Compartir</strong> abajo (el cuadradito con la flecha ↑)</li>
              <li>Bajá y elegí <strong>"Agregar a la pantalla de inicio"</strong></li>
              <li>Tocá <strong>Agregar</strong> arriba a la derecha</li>
            </ol>
            <button className="va-btn va-btn-copy" style={{ width: '100%' }} onClick={() => setInstrIOSVisible(false)}>Cerrar</button>
          </div>
        </div>
      )}
    </>
  )
}

// ═══════════════════════════════ Subcomponents ═══════════════════════════

function PedidoCard({ p }) {
  const cfg = ESTADO_PILL[p.estado] || { cls: 'va-pill-mute', label: p.estado }
  const comision = Number(p.comision || 0)
  return (
    <div className="va-pedido">
      <div className="va-pedido-top">
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="va-pedido-num">{p.numero_orden}</div>
          <div className="va-pedido-cliente">{p.comprador_nombre || '—'}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="va-pedido-monto">{fmtUYU(p.monto_total)}</div>
          {comision > 0 && <div className="va-pedido-comision">+ {fmtUYUDec(comision)} tuyo</div>}
        </div>
      </div>
      <div className="va-pedido-bottom">
        <span className={`va-pill ${cfg.cls}`}>{cfg.label}</span>
        <span className="va-pedido-fecha">{haceCuanto(p.pagado_at || p.created_at)}</span>
      </div>
    </div>
  )
}

function ProductoCard({ p, slug }) {
  const [copiado, setCopiado] = useState(false)
  const codigoUpper = (slug || '').toUpperCase()
  const shareUrl = `https://tienda.limpack.uy/producto/${p.sku}${slug ? `?ref=${codigoUpper}` : ''}`

  const compartir = async (e) => {
    e.preventDefault()
    const msg = `${p.nombre}${p.marca ? ' · ' + p.marca : ''} — ${fmtUYU(p.precio_publico)}\n${shareUrl}`
    if (navigator.share) {
      try { await navigator.share({ title: p.nombre, url: shareUrl, text: msg }); return } catch { /* cancelado */ }
    }
    navigator.clipboard?.writeText(shareUrl)
    setCopiado(true); setTimeout(() => setCopiado(false), 2000)
  }

  return (
    <div className="va-prod">
      <a
        href={shareUrl} target="_blank" rel="noreferrer"
        className="va-prod-img"
        title={`Abrir ficha de ${p.nombre}`}
      >
        {p.imagen_url
          ? <img src={p.imagen_url} alt={p.nombre} loading="lazy" />
          : <div className="va-prod-noimg">📦</div>}
        {Number(p.comision_pct) > 0 && (
          <span className="va-prod-pct">{Number(p.comision_pct)}%</span>
        )}
      </a>
      <div className="va-prod-body">
        <div className="va-prod-nombre">{p.nombre}</div>
        <div className="va-prod-precio">{fmtUYU(p.precio_publico)}</div>
        {Number(p.comision_por_unidad) > 0 && (
          <div className="va-prod-ganas">+ {fmtUYUDec(p.comision_por_unidad)} por unidad</div>
        )}
        <button className="va-prod-share" onClick={compartir}>
          {copiado ? '✓ Copiado' : '🔗 Compartir'}
        </button>
      </div>
    </div>
  )
}

function EmptyPedidos() {
  return (
    <div className="va-empty">
      <div style={{ fontSize: 40, opacity: 0.4, marginBottom: 8 }}>🛒</div>
      Todavía no hay pedidos con tu código.<br />
      Compartí tu link y arrancá.
    </div>
  )
}

function BottomNav({ tab, onTab }) {
  const items = [
    { k: 'inicio', l: 'Inicio', i: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 12l9-9 9 9M5 10v10a1 1 0 001 1h4v-7h4v7h4a1 1 0 001-1V10" />
      </svg>
    ) },
    { k: 'ventas', l: 'Ventas', i: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3h2l3 12h11l3-8H7" /><circle cx="9" cy="20" r="1.5" /><circle cx="18" cy="20" r="1.5" />
      </svg>
    ) },
    { k: 'tienda', l: 'Tienda', i: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 7h16l-1 12H5L4 7zM9 7V5a3 3 0 016 0v2" />
      </svg>
    ) },
    { k: 'datos', l: 'Mis datos', i: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0116 0" />
      </svg>
    ) },
  ]
  return (
    <nav className="va-nav">
      {items.map(x => (
        <button key={x.k} className={`va-nav-btn ${tab === x.k ? 'is-active' : ''}`} onClick={() => onTab(x.k)}>
          {x.i}
          <span>{x.l}</span>
        </button>
      ))}
    </nav>
  )
}

// ═══════════════════════════════ CSS ═════════════════════════════════════

const css = `
  .va-app {
    min-height: 100vh; background: #F8FAFC; color: #0F1E36;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    -webkit-font-smoothing: antialiased;
    padding-bottom: 78px;
    max-width: 640px; margin: 0 auto;
    position: relative;
  }
  .va-scroll {
    min-height: calc(100vh - 78px);
  }

  .va-loading, .va-error {
    min-height: 100vh; display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    color: #64748B; font-size: 14px; font-family: 'Inter', sans-serif;
  }
  .va-spinner {
    width: 32px; height: 32px; border-radius: 50%;
    border: 3px solid #E2E8F0; border-top-color: #00A8B5;
    animation: spin 0.8s linear infinite; margin-bottom: 12px;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  .va-impersonar {
    background: #FEF3C7; border-bottom: 1px solid #FDE68A;
    padding: 10px 16px; font-size: 12px; color: #78350F;
    display: flex; justify-content: space-between; align-items: center; gap: 12px;
  }
  .va-impersonar a { color: #78350F; font-weight: 600; text-decoration: underline; }

  /* ─── Hero ─── */
  .va-hero {
    background: linear-gradient(135deg, #1A3461 0%, #00A8B5 100%);
    color: #fff; padding: 24px 20px 28px;
    position: relative; overflow: hidden;
  }
  .va-hero::before {
    content: ''; position: absolute; top: -80px; right: -80px;
    width: 220px; height: 220px;
    background: radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%);
    border-radius: 50%; pointer-events: none;
  }
  .va-hero-top {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 20px; position: relative; z-index: 1;
  }
  .va-brand { font-size: 14px; font-weight: 600; opacity: 0.85; }
  .va-brand em { font-style: normal; opacity: 0.7; }
  .va-avatar {
    width: 36px; height: 36px; border-radius: 50%;
    background: rgba(255,255,255,0.2);
    display: flex; align-items: center; justify-content: center;
    font-weight: 700; font-size: 14px;
    border: 1.5px solid rgba(255,255,255,0.4);
  }
  .va-saludo { font-size: 14px; opacity: 0.8; margin: 0 0 4px; position: relative; z-index: 1; }
  .va-nombre { font-size: 22px; font-weight: 700; margin: 0 0 20px; position: relative; z-index: 1; }
  .va-metric-label {
    font-size: 12px; opacity: 0.75; text-transform: uppercase;
    letter-spacing: 0.08em; font-weight: 500; margin-bottom: 4px;
    position: relative; z-index: 1;
  }
  .va-metric-big {
    font-size: 48px; font-weight: 800; line-height: 1; margin-bottom: 8px;
    letter-spacing: -0.02em; position: relative; z-index: 1;
    font-variant-numeric: tabular-nums;
  }
  .va-metric-delta {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(255,255,255,0.15);
    padding: 6px 12px; border-radius: 999px;
    font-size: 12px; font-weight: 600; position: relative; z-index: 1;
  }
  .va-mini-kpis {
    display: flex; gap: 14px; margin-top: 20px;
    padding-top: 20px; border-top: 1px solid rgba(255,255,255,0.15);
    position: relative; z-index: 1;
  }
  .va-mini-kpi { flex: 1; }
  .va-mini-val { font-size: 18px; font-weight: 700; line-height: 1.1; }
  .va-mini-lbl { font-size: 11px; opacity: 0.75; margin-top: 2px; }

  /* ─── Share card ─── */
  .va-share {
    background: #fff; margin: -18px 16px 16px;
    border-radius: 16px; box-shadow: 0 8px 24px rgba(15, 30, 54, 0.08);
    padding: 18px; position: relative; z-index: 2;
  }
  .va-share-title {
    font-size: 13px; font-weight: 600; color: #64748B;
    text-transform: uppercase; letter-spacing: 0.05em; margin: 0 0 12px;
  }
  .va-share-row { display: flex; gap: 14px; align-items: center; }
  .va-qr {
    width: 88px; height: 88px; background: #F1F5F9; border-radius: 12px;
    flex-shrink: 0; display: flex; align-items: center; justify-content: center; padding: 6px;
  }
  .va-qr img { width: 100%; height: 100%; }
  .va-share-info { flex: 1; min-width: 0; }
  .va-share-codigo {
    font-size: 28px; font-weight: 800; color: #1A3461;
    letter-spacing: 0.02em; line-height: 1;
  }
  .va-share-link {
    font-size: 12px; color: #64748B; margin-top: 6px;
    word-break: break-all; font-family: 'SFMono-Regular', Menlo, monospace;
  }
  .va-share-actions {
    display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 14px;
  }
  .va-btn {
    display: flex; align-items: center; justify-content: center; gap: 6px;
    padding: 12px 14px; border-radius: 10px; font-size: 13px; font-weight: 600;
    cursor: pointer; border: none; font-family: inherit; text-decoration: none;
  }
  .va-btn-wa { background: #25D366; color: #fff; }
  .va-btn-copy { background: #F1F5F9; color: #1A3461; }

  /* ─── Sections ─── */
  .va-section { padding: 0 16px 16px; }
  .va-section-title {
    font-size: 13px; font-weight: 600; color: #64748B;
    text-transform: uppercase; letter-spacing: 0.05em;
    margin: 20px 0 10px;
    display: flex; align-items: center; justify-content: space-between;
  }
  .va-section-title a {
    font-size: 12px; color: #00A8B5; font-weight: 600;
    text-transform: none; letter-spacing: 0; text-decoration: none; cursor: pointer;
  }
  .va-empty {
    padding: 32px 20px; text-align: center;
    color: #94A3B8; font-size: 13px; line-height: 1.5;
  }

  /* ─── Pedido card ─── */
  .va-pedido {
    background: #fff; border-radius: 12px; padding: 12px 14px; margin-bottom: 8px;
    box-shadow: 0 1px 3px rgba(15,30,54,0.05);
  }
  .va-pedido-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; }
  .va-pedido-num {
    font-size: 11px; color: #64748B;
    font-family: 'SFMono-Regular', Menlo, monospace; margin-bottom: 2px;
  }
  .va-pedido-cliente { font-size: 14px; font-weight: 600; color: #0F1E36; }
  .va-pedido-monto { font-size: 15px; font-weight: 700; color: #1A3461; text-align: right; }
  .va-pedido-comision {
    font-size: 11px; color: #00A8B5; font-weight: 700;
    text-align: right; margin-top: 2px;
  }
  .va-pedido-bottom {
    display: flex; justify-content: space-between; align-items: center;
    margin-top: 8px; gap: 8px;
  }
  .va-pedido-fecha { font-size: 11px; color: #64748B; }

  .va-pill {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 3px 9px; border-radius: 999px;
    font-size: 11px; font-weight: 600; white-space: nowrap;
  }
  .va-pill-ok   { background: #D1FAE5; color: #065F46; }
  .va-pill-warn { background: #FEF3C7; color: #92400E; }
  .va-pill-info { background: #E0E7FF; color: #3730A3; }
  .va-pill-err  { background: #FEE2E2; color: #991B1B; }
  .va-pill-mute { background: #F1F5F9; color: #64748B; }

  /* ─── Producto card ─── */
  .va-prod-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
  }
  .va-prod {
    background: #fff; border-radius: 12px; overflow: hidden;
    box-shadow: 0 1px 3px rgba(15,30,54,0.05);
    display: flex; flex-direction: column;
  }
  .va-prod-img {
    aspect-ratio: 1; background: #F1F5F9;
    display: flex; align-items: center; justify-content: center;
    position: relative; overflow: hidden; text-decoration: none;
  }
  .va-prod-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .va-prod-noimg { font-size: 40px; opacity: 0.4; }
  .va-prod-pct {
    position: absolute; top: 8px; right: 8px;
    background: #00A8B5; color: #fff;
    padding: 3px 8px; border-radius: 999px;
    font-size: 10px; font-weight: 700;
  }
  .va-prod-body {
    padding: 10px 12px 12px; flex: 1;
    display: flex; flex-direction: column;
  }
  .va-prod-nombre {
    font-size: 12px; font-weight: 600; color: #1A3461;
    line-height: 1.3; min-height: 2.6em;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  }
  .va-prod-precio { font-size: 14px; font-weight: 700; color: #1A3461; margin-top: 6px; }
  .va-prod-ganas { font-size: 10px; color: #00A8B5; font-weight: 600; margin-top: 2px; }
  .va-prod-share {
    margin-top: 10px; padding: 8px;
    background: #F1F5F9; border-radius: 8px;
    text-align: center; font-size: 11px; font-weight: 600;
    color: #1A3461; cursor: pointer; border: none; font-family: inherit;
  }
  .va-prod-share:hover { background: #E2E8F0; }

  /* ─── Historial ─── */
  .va-hist-item {
    background: #fff; border-radius: 10px; padding: 12px 14px; margin-bottom: 6px;
    display: flex; justify-content: space-between; align-items: center;
    box-shadow: 0 1px 2px rgba(15,30,54,0.04);
  }
  .va-hist-mes { font-size: 13px; color: #0F1E36; text-transform: capitalize; font-weight: 500; }
  .va-hist-sub { color: #64748B; font-size: 11px; margin-top: 2px; }
  .va-hist-monto { font-size: 15px; font-weight: 700; color: #1A3461; }
  .va-hist-estado {
    display: inline-block; font-size: 10px; font-weight: 600;
    padding: 2px 6px; border-radius: 4px; margin-top: 2px;
  }

  /* ─── Chips filtro ─── */
  .va-chips {
    display: flex; gap: 6px; overflow-x: auto;
    padding: 12px 16px; scrollbar-width: none;
  }
  .va-chips::-webkit-scrollbar { display: none; }
  .va-chip {
    padding: 8px 14px; border-radius: 999px;
    font-size: 12px; font-weight: 600;
    background: #fff; color: #64748B;
    border: 1px solid #E2E8F0;
    white-space: nowrap; cursor: pointer; font-family: inherit;
  }
  .va-chip.is-active { background: #1A3461; color: #fff; border-color: #1A3461; }

  /* ─── Subheader ─── */
  .va-subheader {
    background: #1A3461; color: #fff; padding: 18px 20px 16px;
  }
  .va-subheader h2 { margin: 0; font-size: 18px; font-weight: 700; }
  .va-subheader p { margin: 4px 0 0; font-size: 12px; opacity: 0.75; }

  /* ─── Search ─── */
  .va-search {
    width: 100%; padding: 11px 14px;
    border: 1px solid #E2E8F0; border-radius: 10px;
    font-size: 14px; font-family: inherit; background: #fff;
    box-sizing: border-box;
  }
  .va-search:focus { outline: none; border-color: #00A8B5; }

  /* ─── Datos card ─── */
  .va-datos-card {
    background: #fff; border-radius: 14px; padding: 18px;
    margin: 16px; box-shadow: 0 1px 3px rgba(15,30,54,0.05);
  }
  .va-datos-header {
    display: flex; align-items: center; gap: 14px; margin-bottom: 14px;
  }
  .va-datos-avatar {
    width: 56px; height: 56px; border-radius: 50%;
    background: linear-gradient(135deg, #1A3461, #00A8B5); color: #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 22px; font-weight: 700;
  }
  .va-datos-nombre { font-size: 16px; font-weight: 700; color: #1A3461; }
  .va-datos-sub { font-size: 12px; color: #64748B; }
  .va-datos-row {
    display: flex; justify-content: space-between; padding: 10px 0;
    border-bottom: 1px solid #E2E8F0; font-size: 13px;
  }
  .va-datos-row:last-child { border-bottom: none; }
  .va-datos-row .k { color: #64748B; }
  .va-datos-row .v { color: #0F1E36; font-weight: 600; text-align: right; word-break: break-all; }

  .va-datos-titulo-acciones {
    padding: 16px 16px 8px; font-size: 12px; color: #64748B;
    text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600;
  }
  .va-datos-action {
    display: block; width: calc(100% - 32px); margin: 8px 16px;
    padding: 14px; border-radius: 10px; border: none;
    font-size: 14px; font-weight: 600; cursor: pointer; font-family: inherit;
    text-align: center; text-decoration: none; box-sizing: border-box;
  }
  .va-action-primary { background: #1A3461; color: #fff; }
  .va-action-secondary { background: #fff; color: #1A3461; border: 1px solid #E2E8F0; }
  .va-action-danger { background: #fff; color: #991B1B; border: 1px solid #FECACA; }
  .va-action-install {
    background: linear-gradient(135deg, #1A3461, #00A8B5); color: #fff;
    box-shadow: 0 6px 18px rgba(0,168,181,0.25);
    display: flex; align-items: center; justify-content: center; gap: 8px;
  }
  .va-foot {
    padding: 20px 16px 30px; text-align: center;
    font-size: 11px; color: #94A3B8;
  }

  /* ─── Bottom nav ─── */
  .va-nav {
    position: fixed; bottom: 0; left: 0; right: 0;
    background: #fff; border-top: 1px solid #E2E8F0;
    padding: 8px 4px calc(8px + env(safe-area-inset-bottom));
    display: grid; grid-template-columns: repeat(4, 1fr);
    box-shadow: 0 -4px 12px rgba(15,30,54,0.05);
    z-index: 100;
    max-width: 640px; margin: 0 auto;
  }
  .va-nav-btn {
    display: flex; flex-direction: column; align-items: center; gap: 3px;
    padding: 8px 4px; background: none; border: none;
    cursor: pointer; color: #64748B; font-family: inherit;
    transition: color 0.15s;
  }
  .va-nav-btn.is-active { color: #1A3461; }
  .va-nav-btn.is-active svg { stroke: #00A8B5; }
  .va-nav-btn svg { width: 22px; height: 22px; }
  .va-nav-btn span { font-size: 10px; font-weight: 600; letter-spacing: 0.02em; }

  /* ─── Modal ─── */
  .va-modal-back {
    position: fixed; inset: 0; background: rgba(15,30,54,0.5);
    display: flex; align-items: center; justify-content: center;
    z-index: 200; padding: 20px;
  }
  .va-modal {
    background: #fff; border-radius: 16px; padding: 20px;
    max-width: 380px; width: 100%; box-shadow: 0 20px 40px rgba(0,0,0,0.2);
  }
  .va-modal-title { font-size: 16px; font-weight: 700; color: #1A3461; }

  /* ─── QR ampliado (share card en Inicio) ─── */
  .va-qr-btn {
    position: relative; padding: 6px;
    background: #F1F5F9; border: none; border-radius: 12px;
    cursor: pointer; font-family: inherit;
    width: 88px; height: 88px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; transition: transform 0.15s;
  }
  .va-qr-btn:hover { transform: scale(1.03); }
  .va-qr-btn:active { transform: scale(0.98); }
  .va-qr-btn img { width: 100%; height: 100%; }
  .va-qr-zoom {
    position: absolute; bottom: 2px; right: 2px;
    background: #1A3461; color: #fff;
    width: 20px; height: 20px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; box-shadow: 0 2px 6px rgba(15,30,54,0.3);
  }

  /* ─── Overlay fullscreen del QR ─── */
  .va-qr-back {
    position: fixed; inset: 0;
    background: rgba(15, 30, 54, 0.75); backdrop-filter: blur(4px);
    display: flex; align-items: center; justify-content: center;
    z-index: 300; padding: 20px;
    animation: va-fade 0.15s ease-out;
  }
  @keyframes va-fade { from { opacity: 0; } to { opacity: 1; } }
  .va-qr-modal {
    background: #fff; border-radius: 20px;
    max-width: 380px; width: 100%;
    padding: 24px 20px 20px; position: relative;
    box-shadow: 0 20px 60px rgba(0,0,0,0.3);
    text-align: center;
  }
  .va-qr-close {
    position: absolute; top: 12px; right: 12px;
    width: 32px; height: 32px; border-radius: 50%;
    background: #F1F5F9; border: none; color: #64748B;
    font-size: 16px; cursor: pointer; font-family: inherit;
    display: flex; align-items: center; justify-content: center;
  }
  .va-qr-close:hover { background: #E2E8F0; color: #1A3461; }
  .va-qr-modal-brand {
    font-size: 22px; font-weight: 700; color: #1A3461;
  }
  .va-qr-modal-brand em {
    font-style: normal; color: #00A8B5;
  }
  .va-qr-modal-sub {
    font-size: 12px; color: #64748B; margin-top: 2px; margin-bottom: 20px;
    text-transform: uppercase; letter-spacing: 0.05em; font-weight: 500;
  }
  .va-qr-xl-wrap {
    background: #fff;
    padding: 8px; border: 3px solid #1A3461;
    border-radius: 16px;
    display: inline-block; margin-bottom: 16px;
  }
  .va-qr-xl {
    display: block;
    width: min(280px, 65vw); height: min(280px, 65vw);
  }
  .va-qr-modal-codigo {
    font-size: 32px; font-weight: 800; color: #1A3461;
    letter-spacing: 0.03em; line-height: 1;
  }
  .va-qr-modal-link {
    font-size: 13px; color: #64748B; margin-top: 6px;
    font-family: 'SFMono-Regular', Menlo, monospace;
  }
  .va-qr-actions {
    display: grid; grid-template-columns: 1fr 1fr; gap: 8px;
    margin: 20px 0 12px;
  }
  .va-btn-primary-navy {
    background: #1A3461; color: #fff;
  }
  .va-btn:disabled { opacity: 0.6; cursor: wait; }
  .va-qr-modal-hint {
    font-size: 11px; color: #94A3B8; line-height: 1.5;
    padding: 8px 4px 0;
  }
`
